import { useRef } from 'react';
import { X } from 'lucide-react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { Filters } from './Filters';
import type { FilterState } from '../types';

/**
 * Modale de filtres du module KPI consolidés — utilisée par le tab Adélie
 * pour libérer la largeur principale. Reprend visuellement le composant
 * `Filters` sans modification (même chrome : badge actif, sections
 * pliables, bouton Réinitialiser).
 *
 * Fermeture : click hors modale, Escape, bouton X (haut-droit du bloc).
 * Propagation des updates : **live** — chaque changement de filtre est
 * poussé immédiatement au parent pour que les graphs se mettent à jour
 * sans clic « Appliquer ».
 */
export interface FiltersModalProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  defaults: FilterState;
  onClose: () => void;
  hidePeriodSection?: boolean;
}

export function FiltersModal({
  value,
  onChange,
  defaults,
  onClose,
  hidePeriodSection,
}: FiltersModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-brand/30 px-4 pt-24 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filtres"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm animate-scale-in"
      >
        {/* Bouton X flottant — chevauche légèrement le header interne de
            Filters sans le modifier. Position absolute côté container
            modale pour ne pas altérer le design de `Filters`. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-2 top-2 z-10 rounded-md p-1 text-stripe-text-muted hover:bg-stripe-hover hover:text-stripe-text"
        >
          <X className="h-4 w-4" />
        </button>
        <Filters
          value={value}
          onChange={onChange}
          defaults={defaults}
          hidePeriodSection={hidePeriodSection}
        />
      </div>
    </div>
  );
}
